'use client';

import Link from 'next/link';
import classes from './ContactShowcase.module.scss';
import { IconPhone, IconBrandWhatsapp, IconMail, IconMapPin, IconArrowRight, IconSparkles } from '@tabler/icons-react';

type ContactItem = {
  label: string;
  value: string;
  href?: string;
  type?: 'phone' | 'whatsapp' | 'email' | 'location';
};

type ContactShowcaseProps = {
  title: string;
  description: string;
  contacts: ContactItem[];
  note?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

const getIcon = (type?: string) => {
  switch (type) {
    case 'phone':
      return <IconPhone size={24} stroke={2} />;
    case 'whatsapp':
      return <IconBrandWhatsapp size={24} stroke={2} />;
    case 'email':
      return <IconMail size={24} stroke={2} />;
    case 'location':
      return <IconMapPin size={24} stroke={2} />;
    default:
      return null;
  }
};

export const ContactShowcase = ({ data }: { data: ContactShowcaseProps }) => {
  const { title, description, contacts = [], note, ctaLabel, ctaHref } = data;

  const primaryContact = contacts.find(c => c.type === 'phone' || c.type === 'whatsapp');
  const otherContacts = contacts.filter(c => c !== primaryContact);

  return (
    <section className={classes.section} aria-labelledby="contact-title">
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.badgeWrapper}>
            <IconSparkles size={18} className={classes.badgeIcon} />
            <span className={classes.badge}>Contact</span>
          </div>
          <h2 id="contact-title" className={classes.title}>{title}</h2>
          <p className={classes.description}>{description}</p>
        </div>

        <div className={classes.content}>
          <div className={classes.primaryCard}>
            <div className={classes.primaryHeader}>
              <h3 className={classes.primaryTitle}>Parlez-nous de votre projet</h3>
              <p className={classes.primarySubtitle}>Réponse sous 24h ouvrées</p>
            </div>
            
            {primaryContact && (
              <div className={classes.primaryActions}>
                {primaryContact.type === 'phone' && primaryContact.href && (
                  <a href={primaryContact.href} className={classes.primaryButton}>
                    <IconPhone size={22} stroke={2} />
                    <span>{primaryContact.value}</span>
                    <IconArrowRight size={18} className={classes.arrowIcon} />
                  </a>
                )}
                {primaryContact.type === 'whatsapp' && primaryContact.href && (
                  <a 
                    href={primaryContact.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`${classes.primaryButton} ${classes.whatsappButton}`}
                  >
                    <IconBrandWhatsapp size={22} stroke={2} />
                    <span>WhatsApp</span>
                    <IconArrowRight size={18} className={classes.arrowIcon} />
                  </a>
                )}
              </div>
            )}

            {ctaLabel && ctaHref && (
              <Link href={ctaHref} className={classes.ctaButton}>
                {ctaLabel}
                <IconArrowRight size={20} className={classes.ctaArrow} />
              </Link>
            )}
          </div>

          <div className={classes.secondaryCard}>
            <h4 className={classes.secondaryTitle}>Autres moyens de contact</h4>
            <div className={classes.contactsList}>
              {otherContacts.map((contact) => (
                <a
                  key={contact.label}
                  href={contact.href || '#'}
                  className={classes.contactLink}
                  target={contact.type === 'whatsapp' || contact.type === 'email' ? '_blank' : undefined}
                  rel={contact.type === 'whatsapp' || contact.type === 'email' ? 'noopener noreferrer' : undefined}
                >
                  <div className={classes.contactIconWrapper}>
                    {getIcon(contact.type)}
                  </div>
                  <div className={classes.contactText}>
                    <span className={classes.contactLabel}>{contact.label}</span>
                    <span className={classes.contactValue}>{contact.value}</span>
                  </div>
                  <IconArrowRight size={16} className={classes.linkArrow} />
                </a>
              ))}
            </div>
            {note && <p className={classes.note}>{note}</p>}
          </div>
        </div>
      </div>
    </section>
  );
};
